import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight, CalendarBlank } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/date";

const TYPE_META = {
  "Passport expiration": { color: "#ef4444", short: "Passport" },
  "Visa expiration": { color: "#f59e0b", short: "Visa" },
  "IELTS / PTE": { color: "#0ea5e9", short: "IELTS/PTE" },
  "NAATI": { color: "#d946ef", short: "NAATI" },
  "Skill Assessment": { color: "#6366f1", short: "Skill" },
  "PY": { color: "#14b8a6", short: "PY" },
  "Tuition Fee": { color: "#22c55e", short: "Tuition" },
  "Birthday": { color: "#10b981", short: "Birthday" },
};

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const toKey = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const CalendarView = () => {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), monthIdx: now.getMonth() };
  });
  const [events, setEvents] = useState({});
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/tasks", { params: { days: 365 } }).then((r) => {
      const grouped = {};
      for (const item of r.data.items) {
        (grouped[item.date] = grouped[item.date] || []).push(item);
      }
      setEvents(grouped);
    });
  }, []);

  const cells = useMemo(() => {
    const first = new Date(month.year, month.monthIdx, 1);
    const startDay = (first.getDay() + 6) % 7; // Mon=0
    const daysInMonth = new Date(month.year, month.monthIdx + 1, 0).getDate();
    const c = [];
    for (let i = 0; i < startDay; i++) c.push(null);
    for (let d = 1; d <= daysInMonth; d++) c.push(new Date(month.year, month.monthIdx, d));
    while (c.length % 7 !== 0) c.push(null);
    return c;
  }, [month]);

  const goPrev = () => {
    setSelected(null);
    setMonth((m) => (m.monthIdx === 0 ? { year: m.year - 1, monthIdx: 11 } : { ...m, monthIdx: m.monthIdx - 1 }));
  };
  const goNext = () => {
    setSelected(null);
    setMonth((m) => (m.monthIdx === 11 ? { year: m.year + 1, monthIdx: 0 } : { ...m, monthIdx: m.monthIdx + 1 }));
  };
  const goToday = () => {
    const now = new Date();
    setSelected(toKey(now));
    setMonth({ year: now.getFullYear(), monthIdx: now.getMonth() });
  };

  const todayKey = toKey(new Date());
  const monthEventCount = useMemo(() => {
    return cells.reduce((n, d) => (d && events[toKey(d)] ? n + events[toKey(d)].length : n), 0);
  }, [cells, events]);

  const selectedEvents = selected ? events[selected] || [] : [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,2fr)_minmax(280px,1fr)] gap-6" data-testid="calendar-view">
      <div className="bg-card border border-black/5 dark:border-white/10 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
              <CalendarBlank size={12} weight="duotone" /> Monthly workload
            </p>
            <h3 className="font-heading font-semibold text-2xl mt-1" data-testid="calendar-month-label">
              {MONTH_NAMES[month.monthIdx]} {month.year}
            </h3>
            <p className="text-xs text-muted-foreground mt-1">{monthEventCount} event(s) this month</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={goToday} className="rounded-full text-xs" data-testid="calendar-today-btn">
              Today
            </Button>
            <Button variant="ghost" size="icon" onClick={goPrev} className="rounded-full" data-testid="calendar-prev-btn">
              <CaretLeft size={16} />
            </Button>
            <Button variant="ghost" size="icon" onClick={goNext} className="rounded-full" data-testid="calendar-next-btn">
              <CaretRight size={16} />
            </Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3 text-[10px] text-muted-foreground">
          {Object.entries(TYPE_META).map(([label, meta]) => (
            <span key={label} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} /> {meta.short}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 text-[11px] font-medium text-muted-foreground mb-1">
          {WEEKDAYS.map((d) => (
            <div key={d} className="px-2 py-1 text-center uppercase tracking-wider">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
          {cells.map((d, i) => {
            if (!d) return <div key={`empty-${i}`} className="min-h-[86px] rounded-lg bg-black/[0.02] dark:bg-white/[0.02]" />;
            const key = toKey(d);
            const dayEvents = events[key] || [];
            const isToday = key === todayKey;
            const isSelected = key === selected;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setSelected(isSelected ? null : key)}
                data-testid={`calendar-day-${key}`}
                className={`text-left min-h-[86px] rounded-lg p-2 transition border ${
                  isSelected
                    ? "border-[#007AFF] bg-[#007AFF]/10"
                    : isToday
                    ? "border-white/20 dark:border-white/25 bg-white/[0.04]"
                    : "border-transparent hover:border-white/10 hover:bg-white/[0.03]"
                }`}
              >
                <div className={`flex items-center justify-between text-xs mb-1.5 ${isToday ? "font-semibold" : ""}`}>
                  <span className={isToday ? "text-[#007AFF]" : ""}>{d.getDate()}</span>
                  {dayEvents.length > 0 && (
                    <span className="text-[10px] text-muted-foreground tabular-nums">{dayEvents.length}</span>
                  )}
                </div>
                <div className="flex flex-col gap-0.5">
                  {dayEvents.slice(0, 3).map((e) => (
                    <span
                      key={e.id}
                      title={`${e.client_name} — ${e.type}`}
                      className="truncate text-[10px] rounded px-1.5 py-0.5 leading-tight"
                      style={{
                        background: `${TYPE_META[e.type]?.color || "#64748b"}22`,
                        color: TYPE_META[e.type]?.color || "#64748b",
                      }}
                    >
                      {e.client_name}
                    </span>
                  ))}
                  {dayEvents.length > 3 && (
                    <span className="text-[10px] text-muted-foreground mt-0.5">+{dayEvents.length - 3} more</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="bg-card border border-black/5 dark:border-white/10 rounded-2xl p-6" data-testid="calendar-day-detail">
        <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Selected day</p>
        <h3 className="font-heading font-semibold text-xl mt-1">
          {selected
            ? `${new Date(selected).toLocaleDateString(undefined, { weekday: "long" })} · ${fmtDate(selected)}`
            : "Pick a day"}
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          {selected ? `${selectedEvents.length} event(s)` : "Click any day on the calendar to see its full workload."}
        </p>

        <ul className="mt-5 space-y-2 stagger-in">
          {selectedEvents.map((e) => {
            const meta = TYPE_META[e.type];
            return (
              <li key={e.id} data-testid={`calendar-event-${e.id}`}>
                <Link
                  to={`/clients/${e.client_id}`}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-white/[0.04] transition border border-transparent hover:border-white/10"
                >
                  <span className="w-2 h-8 rounded-full shrink-0" style={{ background: meta?.color || "#64748b" }} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-sm truncate">{e.client_name}</p>
                    <p className="text-xs text-muted-foreground">{e.type}</p>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground tabular-nums">
                    {e.days_left === 0 ? "today" : `${e.days_left}d`}
                  </span>
                </Link>
              </li>
            );
          })}
          {selected && selectedEvents.length === 0 && (
            <li className="text-xs text-muted-foreground py-6 text-center">No expirations on this day.</li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default CalendarView;
